import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Briefcase,
  UserCog,
  CreditCard,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Scan,
  ChevronRight,
  Activity,
  MessageCircle
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/clients', icon: Users, label: 'Clientes' },
  { path: '/agenda', icon: Calendar, label: 'Agenda' },
  { path: '/checkin', icon: Scan, label: 'Check-in' },
  { path: '/live', icon: Activity, label: 'Live', badge: 'Nuevo' },
  { path: '/notifications', icon: MessageCircle, label: 'Notificaciones' },
  { path: '/services', icon: Briefcase, label: 'Servicios' },
  { path: '/staff', icon: UserCog, label: 'Personal' },
  { path: '/memberships', icon: CreditCard, label: 'Membresías' },
  { path: '/reports', icon: BarChart3, label: 'Reportes' },
  { path: '/settings', icon: Settings, label: 'Configuración' },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0c0c0d] flex">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[280px] nova-sidebar
          flex flex-col
          transform lg:transform-none transition-transform duration-300 ease-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-[72px] flex items-center justify-between px-5 border-b border-white/[0.06]">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/30 transition-shadow">
              <span className="text-white font-bold text-base">N</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[17px] font-semibold text-white tracking-tight">Nova</span>
              <span className="text-[10px] text-[#6e6e73] font-medium uppercase tracking-widest">Business Suite</span>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#a1a1a6]" />
          </button>
        </div>

        {/* Business card */}
        <div className="px-4 py-5">
          <div className="p-4 rounded-xl bg-[#161618] border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/20 flex items-center justify-center">
                <span className="text-indigo-400 font-semibold text-sm">
                  {user?.business_name?.charAt(0)?.toUpperCase() || 'N'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.business_name || 'Mi Negocio'}
                </p>
                <p className="text-xs text-[#6e6e73]">
                  Plan {user?.plan === 'trial' ? 'Prueba' : user?.plan?.charAt(0).toUpperCase() + user?.plan?.slice(1) || 'Free'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          <p className="px-3 mb-3 text-[10px] font-semibold text-[#48484d] uppercase tracking-widest">
            Menú
          </p>
          <div className="space-y-1">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03, duration: 0.3 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      nova-sidebar-item
                      ${isActive ? 'nova-sidebar-item-active' : ''}
                    `}
                  >
                    <item.icon className="nova-sidebar-icon" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge && !isActive && (
                      <span className="text-[10px] font-semibold text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {isActive && (
                      <ChevronRight className="w-4 h-4 text-indigo-400" />
                    )}
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#111113]">
            <div className="nova-avatar nova-avatar-md">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-[#6e6e73] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#a1a1a6] hover:text-white hover:bg-white/[0.04] rounded-lg transition-all"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </motion.aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden h-16 bg-[#111113]/95 backdrop-blur-xl border-b border-white/[0.06] flex items-center px-4 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2.5 hover:bg-white/5 rounded-xl transition-colors"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1 flex justify-center">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">N</span>
              </div>
              <span className="text-lg font-semibold text-white">Nova</span>
            </Link>
          </div>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="p-6 lg:p-10 max-w-[1600px] mx-auto"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
